import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Box,
  Typography,
  Alert
} from '@mui/material';
import { GET_CUSTOMER_PRODUCTS, ASSOCIATE_CUSTOMER_WITH_PRODUCT, REMOVE_CUSTOMER_PRODUCT_ASSOCIATION } from '../../../../client/queries/customer_products';
import { SEARCH_PRODUCTS } from '../../../../client/queries/products';

interface Product {
  id: number;
  name: string;
  description: string;
  isarchived: boolean;
}

interface CustomerProductsProps {
  customerId: number;
  customerName: string;
}

const CustomerProducts: React.FC<CustomerProductsProps> = ({ customerId, customerName }) => {
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Queries
  const { refetch: refetchCustomerProducts } = useQuery(GET_CUSTOMER_PRODUCTS, {
    variables: { customerId },
    onCompleted: (data) => {
      if (data?.customerProducts) {
        setSelectedProducts(data.customerProducts.map((p: Product) => p.id));
      }
    }
  });

  const { data: allProductsData } = useQuery(SEARCH_PRODUCTS, {
    variables: { search: '', isarchived: false }
  });

  // Mutations
  const [associateProduct] = useMutation(ASSOCIATE_CUSTOMER_WITH_PRODUCT, {
    onCompleted: (data) => {
      if (data.associateCustomerWithProduct.success) {
        setMessage({ text: data.associateCustomerWithProduct.message, type: 'success' });
        refetchCustomerProducts();
      } else {
        setMessage({ text: data.associateCustomerWithProduct.message, type: 'error' });
      }
    }
  });

  const [removeAssociation] = useMutation(REMOVE_CUSTOMER_PRODUCT_ASSOCIATION, {
    onCompleted: (data) => {
      if (data.removeCustomerProductAssociation.success) {
        setMessage({ text: data.removeCustomerProductAssociation.message, type: 'success' });
        refetchCustomerProducts();
      } else {
        setMessage({ text: data.removeCustomerProductAssociation.message, type: 'error' });
      }
    }
  });

  useEffect(() => {
    if (allProductsData?.searchProducts) {
      setAvailableProducts(allProductsData.searchProducts);
    }
  }, [allProductsData]);

  const handleProductChange = (event: SelectChangeEvent<typeof selectedProducts>) => {
    const value = event.target.value;
    const newSelectedProducts = typeof value === 'string' ? value.split(',').map(Number) : value;
    
    // Find products to add and remove
    const toAdd = newSelectedProducts.filter(id => !selectedProducts.includes(id));
    const toRemove = selectedProducts.filter(id => !newSelectedProducts.includes(id));

    // Execute mutations
    toAdd.forEach(productId => {
      associateProduct({ variables: { customerId, productId } });
    });

    toRemove.forEach(productId => {
      removeAssociation({ variables: { customerId, productId } });
    });

    setSelectedProducts(newSelectedProducts);
  };

  const getProductName = (productId: number): string => {
    const product = availableProducts.find(p => p.id === productId);
    return product ? product.name : `Product ${productId}`;
  };

  return (
    <Box sx={{ minWidth: 300, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Product Access for {customerName}
      </Typography>
      
      {message && (
        <Alert 
          severity={message.type} 
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      <FormControl fullWidth>
        <InputLabel>Associated Products</InputLabel>
        <Select
          multiple
          value={selectedProducts}
          onChange={handleProductChange}
          input={<OutlinedInput label="Associated Products" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={getProductName(value)} />
              ))}
            </Box>
          )}
        >
          {availableProducts.map((product) => (
            <MenuItem key={product.id} value={product.id}>
              {product.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Customers associated with products will automatically receive access to all current and future releases of those products.
      </Typography>
    </Box>
  );
};

export default CustomerProducts;